import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import {
  BodyL,
  HeadlineM,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';

const Root = styled(Vertical)`
  color: var(--c-nd-700);
`;

type Props = {
  collectionEntries: { inSeason: number; classic: number };
  freeEntries: number;
  loading: boolean;
  inSeasonValue?: number;
};

export const TotalEntries = ({
  collectionEntries,
  inSeasonValue = 1,
  freeEntries,
  loading,
}: Props) => {
  return (
    <Root center gap={2}>
      <Vertical center>
        <LabelM>
          <FormattedMessage
            id="NBA.CollectionEvent.InSeasonCollectionEntries"
            defaultMessage="In-Season collections: {nb}"
            values={{
              nb: (
                <Skeleton loading={loading}>
                  <LabelM as="span" color="var(--c-yellow-400)">
                    {collectionEntries.inSeason}
                  </LabelM>{' '}
                  <LabelS as="span" color="var(--c-nd-600)">
                    x{' '}
                    <FormattedMessage
                      id="CollectionEvent.Entries"
                      defaultMessage="{value, plural, =1 {1 entry} other {# entries}}"
                      values={{
                        value: inSeasonValue,
                      }}
                    />
                  </LabelS>
                </Skeleton>
              ),
            }}
          />
        </LabelM>
        <LabelM>
          <FormattedMessage
            id="NBA.CollectionEvent.ClassicCollectionEntries"
            defaultMessage="Classic collections: {nb}"
            values={{
              nb: (
                <Skeleton loading={loading}>
                  <LabelM as="span" color="var(--c-yellow-400)">
                    {collectionEntries.classic}
                  </LabelM>{' '}
                  <LabelS as="span" color="var(--c-nd-600)">
                    x{' '}
                    <FormattedMessage
                      id="CollectionEvent.Entries"
                      defaultMessage="{value, plural, =1 {1 entry} other {# entries}}"
                      values={{
                        value: 1,
                      }}
                    />
                  </LabelS>
                </Skeleton>
              ),
            }}
          />
        </LabelM>
        {freeEntries > 0 && (
          <>
            <HeadlineM>
              <FormattedMessage
                id="NBA.CollectionEvent.CollectionEntries"
                defaultMessage="Collection entries: {nb}"
                values={{
                  nb: (
                    <Skeleton loading={loading}>
                      <HeadlineM as="span" color="var(--c-yellow-400)">
                        {collectionEntries.inSeason * inSeasonValue +
                          collectionEntries.classic}
                      </HeadlineM>
                    </Skeleton>
                  ),
                }}
              />
            </HeadlineM>
            <LabelS>
              <FormattedMessage
                id="NBA.CollectionEvent.PlusFreeEntry"
                defaultMessage="{nb} <green>free</green> entry"
                values={{
                  nb: (
                    <LabelS as="span" bold>
                      {freeEntries}
                    </LabelS>
                  ),
                  green: (chunks: ReactNode[]) => {
                    return (
                      <LabelS as="span" bold color="var(--c-onBase-success)">
                        {chunks}
                      </LabelS>
                    );
                  },
                }}
              />
            </LabelS>
          </>
        )}
      </Vertical>
      <Chip size="large" color="yellow">
        <FormattedMessage
          id="NBA.CollectionEvent.TotalEntries"
          defaultMessage="Total entries: {nb}"
          values={{
            nb: (
              <Skeleton loading={loading}>
                <BodyL as="span" bold color="var(--c-yellow-400)">
                  {collectionEntries.inSeason * inSeasonValue +
                    collectionEntries.classic +
                    freeEntries}
                </BodyL>
              </Skeleton>
            ),
          }}
        />
      </Chip>
    </Root>
  );
};
